import React, { Fragment, ReactNode, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { DataRow } from 'src/components/shared/DataRow'
import { DateTime } from 'luxon'
import { vehicleNamesMap } from 'src/constants/vehicle'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { FetchSensitiveData } from 'src/constants/actionTypes'

import {
  CollectedData,
  FormCollectedData,
  InsuranceBeneficiaryCollectedData,
  TrainingCollectedData,
  VaccinationInformationCollectedData,
  BusinessEntityCollectedData,
  InsuranceCoverageCollectedData,
  MCNumberCollectedData,
  DOTNumberCollectedData,
  PIICollectionCollectedData,
} from 'src/interfaces/collectedData'
import {
  CollectDataFormItemType,
  CollectedDataStatus,
  CollectedDataStepType,
  getBackgroundCheckReminderText,
} from 'src/constants/collectedData'
import { ContractorDetails } from 'src/interfaces/contractor'
import { CardType, PaymentMethodTypeEnum } from 'src/interfaces/paymentMethod'
import tw from 'twin.macro'
import { GSIcon, GSIconName } from 'src/components/shared/GSIcon'
import { InfoContainer } from 'src/components/shared/InfoContainer'
import { Typography } from '@mui/material'
import { ContractorVINStatusChip } from './ContractorVINStatusChip'
import { DownloadSelfieButton } from './step-actions/DownloadSelfieButton'
import { SensitiveDataWrapper } from './SensitiveDataWrapper'
import { SensitiveDataRow } from 'src/components/shared/SensitiveDataRow'
import { SensitiveFileRow } from 'src/components/shared/SensitiveFileRow'
import { getFileExtensionFromUrl, getFileNameFromUrl } from 'src/utils/fileDownload'
import { YardstikScreenings } from './YardstikScreenings'
import { replaceTrainingWording } from 'src/utils/certificationWording'

interface Props {
  collectedData: CollectedData
  contractorDetails: ContractorDetails
  header?: ReactNode
  partnerId?: string | null
}

const CARD_BRAND_ICON_MAP: Partial<Record<CardType, GSIconName>> = {
  [CardType.Visa]: 'gs_visa',
  [CardType.Mastercard]: 'gs_mastercard',
}

// TEMP FEATURE FLAG
const showDownloadSelfieButtonPartnerIds = [
  '2fc98fcb-debc-4abd-96fd-496b1b8401af',
  '70e4d369-e0db-43cd-8f63-ebc8a98f6abe',
]

const stepStatusesToShowReportStatus = [CollectedDataStatus.InProgress, CollectedDataStatus.Created]

const isTrainingDocument = (collectedData: CollectedData): collectedData is TrainingCollectedData =>
  collectedData.collectDataStep.type === CollectedDataStepType.Training

const isBusinessEntityDocument = (
  collectedData: CollectedData,
): collectedData is BusinessEntityCollectedData =>
  collectedData.collectDataStep.type === CollectedDataStepType.BusinessEntity

const isInsuranceBeneficiaryDocument = (
  collectedData: CollectedData,
): collectedData is InsuranceBeneficiaryCollectedData =>
  collectedData.collectDataStep.type === CollectedDataStepType.InsuranceBeneficiary

const isVaccinationInformationDocument = (
  collectedData: CollectedData,
): collectedData is VaccinationInformationCollectedData =>
  collectedData.collectDataStep.type === CollectedDataStepType.VaccinationInformation

const isFormDocument = (collectedData: CollectedData): collectedData is FormCollectedData =>
  collectedData.collectDataStep.type === CollectedDataStepType.Form

const isInsuranceCoverageDocument = (
  collectedData: CollectedData,
): collectedData is InsuranceCoverageCollectedData =>
  collectedData.collectDataStep.type === CollectedDataStepType.InsuranceCoverage

const isMCNumberDocument = (collectedData: CollectedData): collectedData is MCNumberCollectedData =>
  collectedData.collectDataStep.type === CollectedDataStepType.MCNumber

const isDOTNumberDocument = (
  collectedData: CollectedData,
): collectedData is DOTNumberCollectedData =>
  collectedData.collectDataStep.type === CollectedDataStepType.DOTNumber

const isPIICollection = (
  collectedData: CollectedData,
): collectedData is PIICollectionCollectedData =>
  collectedData.collectDataStep.type === CollectedDataStepType.PIICollection

const SSN_MASK = '•••••••••'

export const ContractorStepDataComponent: React.FC<Props> = ({
  collectedData,
  contractorDetails,
  partnerId,
}) => {
  const dispatch = useDispatch()

  const handleFetchAllSensitiveData = useCallback(async () => {
    const response = await createAsyncAction<any, any>(
      dispatch,
      FetchSensitiveData.request({
        partnerDriverId: contractorDetails.id,
        stepId: collectedData.collectDataStep.id,
      }),
    )

    const sensitiveDataMap: Record<string, string> = {}

    const items = response?.data || []

    if (items.length > 0) {
      items.forEach((item: any) => {
        const key = item?.field
        const value = item?.value

        if (typeof key === 'string' && typeof value === 'string') {
          sensitiveDataMap[key] = value
        }
      })
    }

    const files = response?.files || []
    const filesArray: string[] = Array.isArray(files)
      ? files.filter((fileUrl): fileUrl is string => typeof fileUrl === 'string')
      : []

    return {
      data: sensitiveDataMap,
      files: filesArray,
    }
  }, [dispatch, contractorDetails.id, collectedData.collectDataStep.id])

  if (isTrainingDocument(collectedData)) {
    const dateSubmitted = collectedData.finishedAt

    return (
      <div>
        <DataRow
          title="Date submitted"
          text={dateSubmitted ? DateTime.fromISO(dateSubmitted).toFormat('LLL dd, yyyy') : '-'}
        />
      </div>
    )
  }

  if (isInsuranceBeneficiaryDocument(collectedData)) {
    if (!collectedData.metadata?.firstName && !collectedData.metadata?.lastName) {
      return null
    }

    return (
      <div>
        <DataRow title="First name" text={collectedData.metadata?.firstName} />
        <DataRow title="Last name" text={collectedData.metadata?.lastName} />
      </div>
    )
  }

  if (isVaccinationInformationDocument(collectedData)) {
    const isVaccinated = collectedData.metadata?.isVaccinated

    return (
      <div>
        <DataRow title="Vaccinated" text={isVaccinated ? 'YES' : 'NO'} />
        {isVaccinated && (
          <div>
            <DataRow title="Date of most recent vaccination" text={collectedData.metadata?.date} />
            <DataRow title="Vaccine brand you received" text={collectedData.metadata?.brand} />
          </div>
        )}
      </div>
    )
  }

  if (collectedData.collectDataStep.type === CollectedDataStepType.VehicleAndInsurance) {
    return (
      <div>
        <DataRow
          title="Vehicle type"
          text={
            contractorDetails?.vehicle?.type
              ? vehicleNamesMap[contractorDetails?.vehicle?.type]
              : '-'
          }
        />
        {!!contractorDetails?.vehicle?.year && (
          <DataRow title="Vehicle year" text={contractorDetails?.vehicle?.year} />
        )}
        {!!contractorDetails?.vehicle?.carMake && (
          <DataRow title="Make" text={contractorDetails?.vehicle?.carMake} />
        )}
        {!!contractorDetails?.vehicle?.carModel && (
          <DataRow title="Model" text={contractorDetails?.vehicle?.carModel} />
        )}
        {!!contractorDetails?.vehicle?.vin && (
          <DataRow
            title="VIN"
            component={
              <div css={[tw`flex flex-row items-center`]}>
                <ContractorVINStatusChip vinVerified={contractorDetails?.vehicle?.vinVerified} />
                <Typography css={tw`ml-4`} variant="body2">
                  {contractorDetails?.vehicle?.vin}
                </Typography>
              </div>
            }
          />
        )}
        {!!contractorDetails?.vehicle?.wmi && (
          <DataRow title="WMI" text={contractorDetails?.vehicle?.wmi} />
        )}
        {!!contractorDetails?.vehicle?.axles && (
          <DataRow title="Axles" text={contractorDetails?.vehicle?.axles} />
        )}
        {!!contractorDetails?.vehicle?.gvwr && (
          <DataRow title="GVWR" text={contractorDetails?.vehicle?.gvwr} />
        )}
      </div>
    )
  }

  if (
    collectedData.collectDataStep.type === CollectedDataStepType.BackgroundCheck ||
    collectedData.collectDataStep.type === CollectedDataStepType.MvrUpload
  ) {
    const yardstikReport = collectedData.collectDataStep.backgroundCheckReport ?? null

    if (
      !collectedData.dataStatus ||
      !stepStatusesToShowReportStatus.includes(collectedData.status)
    ) {
      if (yardstikReport) {
        return <YardstikScreenings yardstikReport={yardstikReport} />
      }

      return null
    }

    const reminderText = getBackgroundCheckReminderText(
      collectedData.dataStatus,
      collectedData.status,
    )

    return (
      <Fragment>
        {reminderText && (
          <div css={[tw`py-3`]}>
            <InfoContainer>{reminderText}</InfoContainer>
          </div>
        )}
        {yardstikReport && <YardstikScreenings yardstikReport={yardstikReport} />}
      </Fragment>
    )
  }

  if (isFormDocument(collectedData)) {
    if (!collectedData?.metadata?.formItems) {
      return null
    }

    return (
      <div>
        {collectedData?.metadata?.formItems.map((item) => {
          let text: string | number = ''

          if (
            item.type === CollectDataFormItemType.File ||
            item.type === CollectDataFormItemType.Video
          ) {
            return null
          }

          if (
            item.type === CollectDataFormItemType.Radio ||
            item.type === CollectDataFormItemType.Select
          ) {
            text = replaceTrainingWording(item.value.title)
          }

          if (item.type === CollectDataFormItemType.Checkbox) {
            text = item.value
              .map((val) => {
                return replaceTrainingWording(val.title)
              })
              .join(', ')
          }

          if (
            item.type === CollectDataFormItemType.Number ||
            item.type === CollectDataFormItemType.TextInput ||
            item.type === CollectDataFormItemType.Date
          ) {
            text = item.value
          }

          return (
            <DataRow
              direction="column"
              key={item.id}
              title={replaceTrainingWording(item.title)}
              text={text}
            />
          )
        })}
      </div>
    )
  }

  if (isBusinessEntityDocument(collectedData)) {
    return (
      <div>
        <DataRow title="Business entity" text={collectedData.metadata ? 'Yes' : 'No'} />
        {collectedData.metadata?.businessName ? (
          <DataRow title="Business name" text={collectedData.metadata?.businessName} />
        ) : null}
        {collectedData.metadata?.streetAddress ? (
          <DataRow title="Street address" text={collectedData.metadata?.streetAddress} />
        ) : null}
        {collectedData.metadata?.city ? (
          <DataRow title="City" text={collectedData.metadata?.city} />
        ) : null}
        {collectedData.metadata?.state ? (
          <DataRow title="State" text={collectedData.metadata?.state} />
        ) : null}
        {collectedData.metadata?.zipCode ? (
          <DataRow title="Zip code" text={collectedData.metadata?.zipCode} />
        ) : null}
      </div>
    )
  }

  if (isInsuranceCoverageDocument(collectedData)) {
    if (!collectedData.metadata) {
      return null
    }

    return (
      <div>
        <DataRow
          title="Use GigSafe insurance"
          text={collectedData.metadata?.optOut ? 'NO' : 'YES'}
        />
      </div>
    )
  }

  if (
    collectedData.collectDataStep.type === CollectedDataStepType.DriverLicense &&
    showDownloadSelfieButtonPartnerIds.includes(partnerId as any)
  ) {
    return (
      <div>
        <DataRow
          title="Download selfie"
          component={
            <DownloadSelfieButton
              contractorId={contractorDetails.id}
              stepId={collectedData.collectDataStep.id}
            />
          }
        />
      </div>
    )
  }

  if (isMCNumberDocument(collectedData)) {
    const mcNumber = collectedData.metadata?.mcNumber || 'Not provided'
    const lastSubmittedAt = collectedData.metadata?.lastSubmittedAt

    return (
      <div>
        {mcNumber && <DataRow title="Motor Carrier Number" text={mcNumber} />}
        {lastSubmittedAt && (
          <DataRow
            title="Date of last submitted MCS-150 (Motor Carrier Identification Report)"
            text={lastSubmittedAt}
          />
        )}
      </div>
    )
  }

  if (isDOTNumberDocument(collectedData)) {
    const dotNumber = collectedData.metadata?.dotNumber || 'Not provided'
    const usDotNumber = collectedData.metadata?.usDotNumber || 'Not provided'
    const dotNumberStateOfIssue = collectedData.metadata?.dotNumberStateOfIssue
    const lastSubmittedAt = collectedData.metadata?.lastSubmittedAt

    return (
      <div>
        {dotNumber && <DataRow title="DOT Number" text={dotNumber} />}
        {usDotNumber && <DataRow title="US DOT Number" text={usDotNumber} />}
        {dotNumberStateOfIssue && <DataRow title="State of issue" text={dotNumberStateOfIssue} />}
        {lastSubmittedAt && (
          <DataRow
            title="Date of last submitted MCS-150 (Motor Carrier Identification Report)"
            text={lastSubmittedAt}
          />
        )}
      </div>
    )
  }

  if (collectedData.collectDataStep.type === CollectedDataStepType.PaymentMethod) {
    const paymentMethod = contractorDetails.paymentMethod

    if (!paymentMethod) {
      return null
    }

    if (paymentMethod.paymentMethodType === PaymentMethodTypeEnum.Card) {
      const cardNumberDisplay = paymentMethod.cardNumberLast4
        ? `•• ${paymentMethod.cardNumberLast4}`
        : '-'

      const cardBrandIconName = paymentMethod.cardType
        ? CARD_BRAND_ICON_MAP[paymentMethod.cardType]
        : undefined

      return (
        <div>
          <DataRow title="Type" text="Debit Card" />
          <DataRow
            title="Card number"
            component={
              <div css={tw`flex flex-row items-center gap-2 justify-end`}>
                <Typography variant="body2" css={tw`text-black`}>
                  {cardNumberDisplay}
                </Typography>
                {cardBrandIconName && (
                  <GSIcon iconName={cardBrandIconName} width={40} height={26} />
                )}
              </div>
            }
          />
        </div>
      )
    }

    if (paymentMethod.paymentMethodType === PaymentMethodTypeEnum.BankAccount) {
      const accountNumberDisplay = paymentMethod.bankAccountNumberLast4
        ? `•• ${paymentMethod.bankAccountNumberLast4}`
        : '-'

      return (
        <div>
          <DataRow title="Type" text="Bank Information" />
          <DataRow title="Account number" text={accountNumberDisplay} />
        </div>
      )
    }

    return null
  }

  if (isPIICollection(collectedData)) {
    const streetAddress = collectedData.metadata?.streetAddress
    const city = collectedData.metadata?.city
    const state = collectedData.metadata?.state
    const zipCode = collectedData.metadata?.zipCode
    const hasMetadata = !!collectedData.metadata && Object.keys(collectedData.metadata).length > 0

    return (
      <SensitiveDataWrapper onFetchAllSensitiveData={handleFetchAllSensitiveData}>
        {({ getValueForField, sensitiveFiles }) => {
          const ssnValue = getValueForField('ssn')
          const hasAnyData =
            !!ssnValue || hasMetadata || !!(sensitiveFiles && sensitiveFiles.length > 0)

          if (!hasAnyData) {
            return null
          }

          return (
            <div>
              {ssnValue && (
                <SensitiveDataRow
                  title="Social security number"
                  mask={SSN_MASK}
                  onFetchSensitiveData={() => getValueForField('ssn')}
                />
              )}
              {streetAddress && <DataRow title="Street address" text={streetAddress} />}
              {city && <DataRow title="City" text={city} />}
              {state && <DataRow title="State" text={state} />}
              {zipCode && <DataRow title="Zip code" text={zipCode} />}
              {sensitiveFiles &&
                sensitiveFiles.length > 0 &&
                sensitiveFiles.map((fileUrl, index) => {
                  const fileExtension = getFileExtensionFromUrl(fileUrl)
                  const fileNameFromUrl = getFileNameFromUrl(fileUrl)

                  return (
                    <SensitiveFileRow
                      key={index}
                      onFetchSensitiveFile={() => fileUrl}
                      fileExtension={fileExtension}
                      fileName={fileNameFromUrl}
                    />
                  )
                })}
            </div>
          )
        }}
      </SensitiveDataWrapper>
    )
  }

  return null
}

export const getContractorStepData = ({ collectedData, contractorDetails, partnerId }: Props) => {
  return (
    <ContractorStepDataComponent
      collectedData={collectedData}
      contractorDetails={contractorDetails}
      partnerId={partnerId}
    />
  )
}
